'use client';
import React from 'react';

import CardMetric from '@src/components/Shared/CardMetric';
import UnitDropdown from '../UnitDropdown/UnitDropdown';
import { UnitTypes } from '../UnitDropdown/UnitDropdown.types';
import ForceInput from '../ForceInput/ForceInput';
import useIsMobile from '@src/hooks/useIsMobile';
import useDebounce from '@src/hooks/useDebounce';

const units: UnitTypes[] = [
  { id: 'newtons', name: 'Newtons', unit: 'N' },
  { id: 'pounds', name: 'Pounds', unit: 'lb' },
  { id: 'kilograms', name: 'Kilograms', unit: 'kg' },
];

// conversionRates[from][to]
const conversionRates: Record<string, Record<string, number>> = {
  newtons: { pounds: 0.224809, kilograms: 0.1019, newtons: 1 },
  pounds: { newtons: 4.448, kilograms: 0.4536, pounds: 1 },
  kilograms: { newtons: 9.81, pounds: 2.205, kilograms: 1 },
};

const LoadConversion: React.FC = () => {
  const isMobile = useIsMobile();

  // desktop-persistent vs mobile-temporary
  const [value, setValue] = React.useState('');
  const [tempValue, setTempValue] = React.useState('');

  // unit selectors
  const [inputUnit, setInputUnit] = React.useState<'newtons' | 'pounds' | 'kilograms'>('newtons');
  const [outputUnit, setOutputUnit] = React.useState<'newtons' | 'pounds' | 'kilograms'>('pounds');

  const handleCalculate = () => setValue(tempValue);

  const displayValue = isMobile ? tempValue : value;
  const hasPending = isMobile && tempValue !== value;

  const rawIn = parseFloat(value) || 0;
  const factor = conversionRates[inputUnit][outputUnit] || 0;
  const result = rawIn * factor;
  const formatted = result.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const debounced = useDebounce(formatted, 3000);

  React.useEffect(() => {
    if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
      if (!isNaN(Number(debounced)) && isFinite(Number(debounced))) {
        window.dataLayer.push({
          event: 'msk_calculator_calculation_performed',
          eventCategory: 'msk_calculator_usage',
          eventLabel: 'msk_calculator_load_conversion',
          value: 1,
        });
      }
    }
  }, [debounced, inputUnit, outputUnit, isMobile]);

  const unitMeta = units.find((u) => u.id === inputUnit)!;

  return (
    <CardMetric
      id='load-conversion'
      className='overflow-visible!'
      title='Load Conversion'
      productIcons={[
        {
          alt: 'NordBord App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/6iS357n4siVSm6nFZeci6q/544cd567518357e813ac48f15dae44f4/nordbord-app-icon.svg',
        },
        {
          alt: 'ForceFrame App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/7dxABhvlBmXLF0UuYpAdss/883fc648f9bc2f93d9d05d9095800e67/forceframe-app-icon.svg',
        },
        {
          alt: 'DynaMo App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/5KQtYsCw5caSZvIuohrRmm/c174bb8572e3170009776e40c21f18e9/dynamo-app-icon.svg',
        },
        {
          alt: 'ForceDecks App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/1sZL8SrOEPwzDxDhnJCoy2/d3561b92560bdd3fe2ecd709b6f4604f/forcedecks-app-icon.svg',
        },
      ]}
    >
      <p className='mb-[2rem] text-[1.2rem]'>
        Quickly convert Newtons, Pounds and Kilograms to easily apply testing data in a more
        digestible format.
      </p>

      {/* — Unit selectors — */}
      <div className='mb-[2.4rem] flex flex-col gap-[1.6rem] sm:flex-row'>
        <UnitDropdown
          className='mb-[0]! [&>div>button]:min-w-[8rem]'
          label='Input Measure:'
          units={units}
          activeUnitId={inputUnit}
          onChange={(id) => setInputUnit(id as any)}
        />
        <UnitDropdown
          className='mb-[0]! [&>div>button]:min-w-[8rem]'
          label='Output Measure:'
          units={units}
          activeUnitId={outputUnit}
          onChange={(id) => setOutputUnit(id as any)}
        />
      </div>

      {/* — ForceInput for value — */}
      <div className='mb-[2rem]'>
        <ForceInput
          className='w-full'
          // placeholder={unitMeta.unit}
          labelLines={['Input Value', unitMeta.name]}
          units={[unitMeta]}
          activeUnit={unitMeta.id}
          value={displayValue}
          onUnitSelect={() => {}}
          onChange={(e) => {
            const v = e.target.value;
            isMobile ? setTempValue(v) : setValue(v);
          }}
        />
      </div>

      {/* — Result — */}
      <div className='flex items-center justify-between text-primary'>
        <span className='ml-auto mr-[2rem] text-[1.4rem] font-medium'>Output</span>
        <span className='border-l-[0.2rem] border-[#D9D9D9] pl-[2rem] text-[1.8rem] sm:text-[3rem]'>
          <span className='font-normal'>{formatted}</span>
          <span className='text-[1.6rem] font-light'>
            {units.find((u) => u.id === outputUnit)?.unit}
          </span>
        </span>
      </div>

      <div className='relative flex items-center gap-[1rem]'>
        {/* <div className='absolute inset-x-[-9999px] top-[-2rem] h-[20rem] bg-background-tertiary' /> */}
        {/* <div className='absolute inset-x-[-9999px] top-[-2rem] h-[20rem] bg-[#F8F8F8]' /> */}

        {/* <div className='flex w-full items-center gap-[1.6rem] text-[1.2rem]'>
          <Bookmaker className='relative z-10 hidden h-[2rem] w-[1.6rem] max-w-[1.6rem] shrink-0 text-primary md:block' />

          <p className='text-pretty'>
            <NextLink
              className='font-light underline'
              href='https://valdperformance.com/news/research-summary-what-is-the-dynamic-strength-index'
            >
              Learn more
            </NextLink>{' '}
            about DSI research, application and how to generate a DSI report in VALD Hub.
          </p>
        </div> */}
        {isMobile && (
          <button
            onClick={handleCalculate}
            disabled={!hasPending}
            className='common-btn-transition right-0 bottom-0 relative ml-auto mt-[1.6rem] inline-flex h-[3.7rem] max-h-[3.7rem] items-center justify-center rounded-[0.6rem] border-primary bg-primary px-[2rem] py-[0.8rem] text-[1.6rem] font-normal leading-[1.6rem] tracking-[-0.0304rem] text-[#fff]! hover:border-primary--v2 hover:bg-primary--v2 hover:text-primary--v2'
          >
            Calculate
          </button>
        )}
      </div>
    </CardMetric>
  );
};

export default LoadConversion;
